/**
 * Sentinel i18n — lightweight translation system.
 * Supports 6 languages: English, Spanish, French, German, Mandarin, Arabic.
 * Falls back to English for missing keys.
 */

import { createContext, useContext, useState, useCallback, createElement, type ReactNode } from "react";
import en from "../locales/en.json";
import es from "../locales/es.json";
import fr from "../locales/fr.json";
import de from "../locales/de.json";
import zh from "../locales/zh.json";
import ar from "../locales/ar.json";

export type Locale = "en" | "es" | "fr" | "de" | "zh" | "ar";

export const LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
];

const translations: Record<Locale, Record<string, string>> = {
  en: en as Record<string, string>,
  es: es as Record<string, string>,
  fr: fr as Record<string, string>,
  de: de as Record<string, string>,
  zh: zh as Record<string, string>,
  ar: ar as Record<string, string>,
};

export function t(key: string, locale: Locale = "en", vars?: Record<string, string>): string {
  const dict = translations[locale] ?? translations.en;
  let str = dict[key] ?? translations.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.replace(new RegExp(`\{${k}\}`, "g"), v);
    }
  }
  return str;
}

export function isRTL(locale: Locale): boolean {
  return locale === "ar";
}

interface TranslationContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string>) => string;
  dir: "ltr" | "rtl";
}

const TranslationContext = createContext<TranslationContextValue>({
  locale: "en",
  setLocale: () => {},
  t: (key: string, vars?: Record<string, string>) => t(key, "en", vars),
  dir: "ltr",
});

export function TranslationProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const saved = localStorage.getItem("sentinel-locale") as Locale | null;
      if (saved && translations[saved]) return saved;
    } catch { /* ignore */ }
    return "en";
  });

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { localStorage.setItem("sentinel-locale", l); } catch { /* ignore */ }
  }, []);

  const value: TranslationContextValue = {
    locale,
    setLocale,
    t: useCallback((key: string, vars?: Record<string, string>) => t(key, locale, vars), [locale]),
    dir: isRTL(locale) ? "rtl" : "ltr",
  };

  return createElement(TranslationContext.Provider, { value }, children);
}

export function useTranslation() {
  return useContext(TranslationContext);
}
